/**
 * The HUD's words.
 *
 * `line`: what the page itself says on the message line (P1.9-T13): E pressed with nothing in reach, a verb the
 * server refused without a sentence of its own, and the Stamina bar's empty line, which stands there while the
 * player's own Stamina is 0 (ADR-116 decision 15). They are player copy under ADR-061's amendment "every reply in
 * sentence case". `noStamina` is the author's choice of 2026-10-08 (ADR-116's note "the empty Stamina line"), in
 * place of the design's "No Stamina: you cannot sprint.".
 *
 * `stance`: the stance button's words (P2.2-T13, ADR-073's amendment "the stance: a weapon put away"), every one a
 * draft for the author: the button's accessible name, which stays the same as it toggles, and its tooltip in each
 * stance. `{key}` is the label of the key that toggles it.
 *
 * `flag`: the PvP flag panel's words, approved by the author (ADR-076's note of 2026-09-28, option A): short labels
 * with no full stop that name the state and not the rules; the lines that carry a number of seconds; and the message
 * line's sentences for a request the server refused. `unsafe`, `interruptedUnsafe` and `refused.unsafe` are drafts
 * for the author (ADR-076's amendment of 2026-10-01), as are the key hints (its note of 2026-10-03), whose words
 * continue a key's name in lower case with no full stop. `partyProtected`, `partyFlagged` and `refused.partyProtected` and
 * `refused.partyFlagged` are drafts for the author (ADR-076's amendment of 2026-10-08, E2; his words of 2026-10-15: the
 * status line says it cannot flag or unflag while in a party, the message line says to leave it). `levelLocked` and `refused.level` are drafts for the author (ADR-076's amendment of 2026-10-08, C3, and his
 * word of 2026-10-15); `{level}` is the rules' `pvp_level`. The two say one fact in the two forms the file keeps: a status line with no full stop and a refusal with one. `{seconds}` is a number as the page prints it.
 *
 * `xp`: the XP bar's tooltip (P2.4-T8, the author's ruling of 2026-10-02, ADR-083's note of that date): the level,
 * the XP into it, and the rate with each factor it comes from. Every word is a draft for the author. `{xp}`,
 * `{into}`, `{span}` and `{level}` are numbers as the page prints them, and `{times}` a rate such as "x1.25".
 *
 * `vitals`, `gameMenu`, `xpBar`, `target`, `maintenance`, `levelUp` and `pickUp`: the player card's bar names and level word,
 * the menu button's name, the XP bar's name and the words of its value, the target frame's, the maintenance warning's
 * (`{time}` is a time of day, `{wait}` and `{length}` worded spans, `{clock}` a countdown as "1:05"), the level-up
 * toast's title and its chat line, the title of the burst sign over a character that reached a level (P2.4-T41), and the refused pick-up's two lines (`{clock}` is the countdown). Drafts for the author.
 *
 * `connection`: the connection mark and the message line while the page tries again in the background (P1.9-T32, the
 * author's ruling of 2026-09-25): the mark's word, its attempt (`{attempt}` of `{total}` as numbers), the word when the
 * connection is back, the words for a slow connection and an offline one (P1.9-T46, from the design's `ConnectionMark`), and the line, a draft for the author.
 *
 * `discovery`: the discovery notice (P2.11-T8, ADR-096 decision 15; the design's `Discovery` toast): its title, the
 * author's wording (ADR-096's amendment of 2026-10-05), the line that names the shrine, which also goes to chat, and the XP the server granted for it (P2.11-T16).
 */
export const HUD = {
  line: {
    nothingHere: "Nothing to use here.",
    nothingHappens: "Nothing happens.",
    noStamina: "Out of breath.",
  },
  stance: {
    button: "Weapon drawn",
    typeLine: "Stance",
    drawn: {
      name: "Weapon drawn",
      first: "A left click selects and attacks.",
      second: "{key} sheathes it: a click then only selects.",
      firstTouch: "A tap selects; Attack swings.",
      secondTouch: "Tap Stance to sheathe it, and Attack rests.",
    },
    sheathed: {
      name: "Weapon sheathed",
      first: "A left click only selects, so you can see a player's card without attacking.",
      second: "{key} or any ability draws it.",
      firstTouch: "A tap only selects, so you can see a player's card without attacking.",
      secondTouch: "Tap Stance, or use any ability, to draw it.",
      third: "Being attacked draws it.",
    },
  },
  flag: {
    mark: "PvP flag",
    panel: "PvP flag toggle",
    toFlagged: "Switch to Flagged",
    toProtected: "Switch to Protected",
    cancel: "Cancel",
    interruptedCombat: "Interrupted by combat",
    interruptedHostile: "Interrupted by a Hostile zone",
    locked: "Locked in a Hostile zone",
    combat: "Locked in combat",
    dead: "Locked while dead",
    unsafe: "Reach a safe zone",
    levelLocked: "Cannot flag until level {level}",
    interruptedUnsafe: "Left the safe zone",
    partyProtected: "Cannot flag while in a Protected party",
    partyFlagged: "Cannot unflag while in a Flagged party",
    hints: {
      switch: "to switch",
      cancel: "to cancel",
      close: "to close",
    },
    flaggedIn: "Flagged in {seconds} s",
    protectedIn: "Protected in {seconds} s",
    protectedInstant: "Protected: Instant",
    flaggedInstant: "Flagged: Instant",
    combatant: "Combatant: {seconds} s",
    refused: {
      combat: "Cannot switch in combat.",
      hostileProtected: "Cannot switch to Protected in a Hostile zone.",
      hostileFlagged: "Cannot switch to Flagged in a Hostile zone.",
      dead: "Cannot switch while dead.",
      unsafe: "Reach a safe zone to switch to Protected.",
      partyProtected: "You are in a Protected party. Leave it to flag.",
      partyFlagged: "You are in a Flagged party. Leave it to unflag.",
      level: "Cannot flag until level {level}.",
    },
  },
  xp: {
    states: {
      protected: "Protected",
      flagged: "Flagged",
      combatant: "Combatant",
    },
    level: "Level {level}",
    maxLevel: "Max level · XP {xp} kept",
    progress: "XP {into} / {span}",
    rate: "Current rate {times}",
    fromFlag: "From your flag: {state} {times}",
    factor: "{label}: {times}",
    fromZone: "From the zone",
    fromHostile: "From the zone: Hostile {times}",
    fromBoosts: "From boosts",
    fromWorld: "From the world",
  },
  vitals: {
    health: "Health",
    stamina: "Stamina",
    focus: "Focus",
    level: "Lv",
    inCombat: "In combat",
  },
  gameMenu: "Game menu",
  bagButton: "Open the bag",
  xpBar: {
    name: "Experience",
    level: "Lv",
    xp: "XP",
  },
  target: {
    dummy: "Training dummy",
    health: "Target health",
    frame: "Target",
  },
  maintenance: {
    logOut: "Log out now",
    toastTitle: "Maintenance at {time}.",
    toastIn: "In {wait}.",
    toastInFor: "In {wait}, for {length}.",
    stripTitle: "Maintenance in {wait}.",
    stripText: "You will be logged out at {time}.",
    lastTitle: "Logging out in {clock}.",
    lastText: "Maintenance starts at {time}.",
  },
  connection: {
    reconnecting: "Reconnecting",
    attempt: "{attempt} of {total}",
    restored: "Connected",
    slow: "Slow connection",
    offline: "Offline",
    line: "Disconnected. Trying to reconnect.",
  },
  levelUp: {
    title: "Level {level}",
    sign: "Level up!",
    chat: "You reached level {level}.",
  },
  pickUp: {
    owned: "That doesn't belong to you ({clock}).",
    full: "Your bags are full.",
  },
  discovery: {
    title: "Shrine Discovered",
    text: "You have discovered {name}.",
    aShrine: "a shrine",
    gain: "+{xp} XP",
  },
} as const;
